import React, { useEffect, useRef, useState } from 'react';
import { Paperclip, Send, Loader2, X, FileText } from 'lucide-react';
import { safeFormat } from '@/lib/utils';

// Shared by the team view and the client's link page.
// posts: [{id, author:{kind,name,id}, text, files:[{id, filename, content_type}], created_at, system, mine?}]
export default function YarnThread({ posts, isMine, onSend, onOpenFile, disabled, disabledNote, placeholder }) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const end = useRef(null);
  const fileInput = useRef(null);

  useEffect(() => { end.current && end.current.scrollIntoView({ block: 'end' }); }, [posts.length]);

  const send = async (e) => {
    e && e.preventDefault();
    if (sending || (!text.trim() && !files.length)) return;
    setSending(true);
    setErr(null);
    try {
      await onSend(text, files);
      setText('');
      setFiles([]);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Could not send. Please try again.');
    } finally { setSending(false); }
  };

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1 pb-2">
        {posts.map((p) => {
          if (p.system) {
            return <div key={p.id} className="text-center text-xs text-[#5C6670] py-1">{p.text} · {safeFormat(p.created_at, 'MMM d, h:mm a')}</div>;
          }
          const mine = isMine(p);
          const guest = p.author.kind === 'guest';
          return (
            <div key={p.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${mine ? 'bg-[#1E2A33] text-white' : guest ? 'bg-amber-50 text-[#1E2A33] border border-amber-200' : 'bg-[#F3EFE7] text-[#1E2A33]'}`}>
                <div className={`text-[11px] mb-0.5 ${mine ? 'text-white/60' : 'text-[#5C6670]'}`}>
                  {p.author.name}{guest ? ' (client)' : ''} · {safeFormat(p.created_at, 'MMM d, h:mm a')}
                </div>
                {p.text && <div className="whitespace-pre-wrap leading-relaxed">{p.text}</div>}
                {(p.files || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {p.files.map((f) => (
                      <button key={f.id} type="button" onClick={() => onOpenFile(f)}
                        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${mine ? 'bg-white/10 hover:bg-white/20' : 'bg-white border border-[#E3DDD1] hover:bg-[#FBF8F2]'}`}>
                        <FileText className="w-3.5 h-3.5" />{f.filename}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>

      {disabled ? (
        <p className="text-sm text-[#5C6670] border-t border-[#E3DDD1] pt-3">{disabledNote}</p>
      ) : (
        <form onSubmit={send} className="border-t border-[#E3DDD1] pt-3">
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {files.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-md bg-[#F3EFE7] px-2 py-1 text-xs">
                  {f.name}<button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <button type="button" onClick={() => fileInput.current && fileInput.current.click()} className="p-2.5 rounded-lg border border-[#E3DDD1] text-[#5C6670] hover:text-[#1E2A33]" title="Attach files">
              <Paperclip className="w-4 h-4" />
            </button>
            <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => { setFiles([...files, ...Array.from(e.target.files)].slice(0, 10)); e.target.value = ''; }} />
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={placeholder || 'Add to the yarn…'}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(e); }}
              className="flex-1 rounded-lg border border-[#E3DDD1] px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#A62B1F]/20" />
            <button type="submit" disabled={sending || (!text.trim() && !files.length)} className="p-2.5 rounded-lg bg-[#A62B1F] text-white disabled:opacity-50" title="Send (Ctrl/Cmd + Enter)">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
          {err && <p className="text-xs text-[#A62B1F] mt-1">{err}</p>}
        </form>
      )}
    </div>
  );
}
